import api from '@pray/shared/utils/api';

export type SearchLeaderArtistsResponse = {
  artists: Array<{
    id: string;
    name: string;
    profile_image_url: string | null;
  }>;
};

export type DuplicateForLeaderParams = {
  experimentId: string;
  instanceName?: string;
  artistName: string;
  newName?: string;
};

export type PreviewDuplicateForLeaderParams = {
  experimentId: string;
  instanceName?: string;
  artistName: string;
};

export type PreviewDuplicateForLeaderResponse = {
  data: {
    original_experiment: {
      id: string;
      name: string;
      instance_name: string;
    };
    suggested_name: string;
    leader_resources: {
      artist: {
        id: string;
        name: string;
        profile_image_url: string | null;
      };
      lead_campaign: {
        id: string;
        title: string | null;
      } | null;
      daily_item: {
        id: string;
        title: string | null;
      } | null;
    };
    replacement_counts: {
      artist_id: number;
      lead_campaign_id: number;
      daily_item_id: number;
    };
  };
};

export type DuplicateForLeaderResponse = {
  data: {
    experiment: {
      id: string;
      name: string;
      instance_name: string;
      created_at: string;
    };
    leader_resources: {
      artist: {
        id: string;
        name: string;
        profile_image_url: string | null;
      };
      lead_campaign: {
        id: string;
        title: string | null;
      } | null;
      daily_item: {
        id: string;
        title: string | null;
      } | null;
    };
    replacement_counts: {
      artist_id: number;
      lead_campaign_id: number;
      daily_item_id: number;
    };
  };
};

/**
 * Preview duplicating an experiment for a different leader
 * @param experimentId - The ID of the experiment to duplicate
 * @param instanceName - Optional instance name for faster lookup
 * @param artistName - The name of the leader to duplicate for
 * @returns Preview data with resolved leader info and replacement counts
 */
export async function previewDuplicateForLeader({
  experimentId,
  instanceName,
  artistName,
}: PreviewDuplicateForLeaderParams): Promise<PreviewDuplicateForLeaderResponse> {
  if (!experimentId) throw new Error('Experiment ID is required');
  if (!artistName) throw new Error('Leader name is required');

  const url = `/web/studio/experiments/${experimentId}/duplicate-for-leader/preview`;
  const params = instanceName ? { instance_name: instanceName } : undefined;
  const body = { artist_name: artistName };

  const response = await api.executePostRequest<PreviewDuplicateForLeaderResponse>(url, body, { params });
  return response.data;
}

/**
 * Duplicates an experiment for a different leader
 * @param experimentId - The ID of the experiment to duplicate
 * @param instanceName - Optional instance name for faster lookup
 * @param artistName - The name of the leader to duplicate for
 * @param newName - Optional new name for the duplicate
 * @returns The newly created experiment
 */
export async function duplicateForLeader({
  experimentId,
  instanceName,
  artistName,
  newName,
}: DuplicateForLeaderParams): Promise<DuplicateForLeaderResponse> {
  if (!experimentId) throw new Error('Experiment ID is required');
  if (!artistName) throw new Error('Leader name is required');

  const url = `/web/studio/experiments/${experimentId}/duplicate-for-leader`;
  const params = instanceName ? { instance_name: instanceName } : undefined;
  const body: Record<string, string> = { artist_name: artistName };
  if (newName) body.name = newName;

  const response = await api.executePostRequest<DuplicateForLeaderResponse>(url, body, { params });
  return response.data;
}

/**
 * Search for leader artists by name (typeahead)
 * @param query - The search term to match against leader names
 * @returns List of matching artists
 */
export async function searchLeaderArtists({ query }: { query: string }): Promise<SearchLeaderArtistsResponse> {
  const response = await api.executeGetRequest<{ data: SearchLeaderArtistsResponse }>('/web/studio/artists/search', {
    q: query,
  });
  return response.data.data;
}
